import React from 'react';

import {
  AccessByIdentityItem,
  AccessGrant,
  AccessScope as ApiAccessScope,
  GrantKind,
} from '@ariksa/inventory-core';
import { Center, HStack } from '@chakra-ui/react';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import toLower from 'lodash/toLower';
import { Dict } from 'types/utils';

import { ButtonGroupItem } from 'components/DataEntry/Button/ButtonGroup';
import { AccessScope } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/AccessScope';
import {
  getHighPrivilegeColor,
  highPrivilegesConst,
  renderHighPrivilege,
} from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/utils';
import { toTitleCase, toWordCase } from 'utils/string';

/** A filter choice; an empty value means no filter. */
export interface FilterOption {
  label: string;
  value: string;
}

// Less than the table's default cell padding (16px), and a panel 5% taller
// than the drawer's default (510px), so more principals show without scrolling.
export const CELL_PADDING_Y = '12px';
export const PANEL_HEIGHT = '535px';

/**
 * Styles for a grant row's cell, the same as the table gives a principal's
 * cells, so expanded rows don't change how the columns share the width.
 */
export const grantCellStyles = (textAlign: 'left' | 'center') => ({
  textAlign,
  fontSize: 'tableCell',
  paddingTop: CELL_PADDING_Y,
  paddingBottom: CELL_PADDING_Y,
});

// Narrows a column to its content, so spare width goes to the name columns.
export const FIT_CONTENT = { width: '1%' };

// Side padding of the narrow columns (Account, High Privileges, # Permissions):
// wider than the table's 16px, to give them room from their neighbours.
export const NARROW_PADDING_X = { paddingLeft: '20px', paddingRight: '20px' };

// A narrow column: as wide as its content, with that padding on its header
// and cells.
export const NARROW_COLUMN = {
  ...FIT_CONTENT,
  ...NARROW_PADDING_X,
  cell: NARROW_PADDING_X,
};

/** The API's `privilege` value for any level the High Privileges column shows. */
export const HIGH_PRIVILEGE = 'high';

const API_SCOPES: Record<AccessScope, ApiAccessScope> = {
  all: ApiAccessScope.All,
  internal: ApiAccessScope.ThisAccount,
  external: ApiAccessScope.OtherAccounts,
};

export const toApiScope = (scope: AccessScope): ApiAccessScope =>
  API_SCOPES[scope];

/** The All / This Account / Other Accounts buttons, counted by the API. */
export const getScopeItems = (
  counts: Dict<number> | undefined,
): ButtonGroupItem[] => [
  { label: `All ${counts?.[ApiAccessScope.All] ?? 0}`, value: 'all' },
  {
    label: `This Account ${counts?.[ApiAccessScope.ThisAccount] ?? 0}`,
    value: 'internal',
  },
  {
    label: `Other Accounts ${counts?.[ApiAccessScope.OtherAccounts] ?? 0}`,
    value: 'external',
  },
];

const withCount = (label: string, count = 0) =>
  `${label} · ${count.toLocaleString()}`;

/**
 * Builds a filter's options from the API's counts. The selected value stays
 * listed, with a zero count, when another filter leaves it no principals, so
 * the select never shows a value it has no option for.
 */
const toOptions = (
  allLabel: string,
  total: number,
  counts: Dict<number>,
  selected: string,
  toLabel: (key: string) => string,
  order: (keys: string[]) => string[],
): FilterOption[] => {
  const keys = Object.keys(counts);
  if (selected && !keys.includes(selected)) keys.push(selected);
  return [
    { label: withCount(allLabel, total), value: '' },
    ...order(keys).map(key => ({
      label: withCount(toLabel(key), counts[key]),
      value: key,
    })),
  ];
};

export const getTypeOptions = (
  counts: Dict<number> | undefined,
  total: number,
  selected: string,
  getTypeName: (type: string) => string | null | undefined,
): FilterOption[] => {
  const toLabel = (type: string) => getTypeName(type) ?? type;
  return toOptions('All types', total, counts ?? {}, selected, toLabel, keys =>
    [...keys].sort((a, b) => toLabel(a).localeCompare(toLabel(b))),
  );
};

// Levels follow the badge order; any level the badges don't know goes last.
const privilegeRank = (level: string) => {
  if (level === HIGH_PRIVILEGE) return -1;
  const index = highPrivilegesConst.findIndex(o => toLower(o) === level);
  return index === -1 ? highPrivilegesConst.length : index;
};

export const getPrivilegeOptions = (
  counts: Dict<number> | undefined,
  total: number,
  selected: string,
): FilterOption[] =>
  toOptions(
    'All privileges',
    total,
    counts ?? {},
    selected,
    key => (key === HIGH_PRIVILEGE ? 'Any high privilege' : toTitleCase(key)),
    keys =>
      [...keys].sort(
        (a, b) =>
          privilegeRank(toLower(a)) - privilegeRank(toLower(b)) ||
          a.localeCompare(b),
      ),
  );

/**
 * The privilege options in two groups, so the menu can draw a line between
 * the combined choices (All privileges, Any high privilege) and the levels.
 */
export const groupPrivilegeOptions = (options: FilterOption[]) => {
  const isCombined = (option: FilterOption) =>
    option.value === '' || option.value === HIGH_PRIVILEGE;
  return [
    { label: '', options: options.filter(isCombined) },
    { label: '', options: options.filter(option => !isCombined(option)) },
  ].filter(group => group.options.length);
};

/** Only principals with more than one grant have rows to expand. */
export const hasManyGrants = (row?: Pick<AccessByIdentityItem, 'grants'>) =>
  (row?.grants?.length ?? 0) > 1;

export const isRoleGrant = (grant?: AccessGrant): boolean =>
  grant?.kind === GrantKind.Role;

/**
 * The second line under a grant, naming its kind in title case: "AWS Managed
 * Policy", "Customer Managed Policy" or "Assumed Role". The API sends only who
 * manages a policy (`managed_by`, such as AWS or Customer).
 */
export const getGrantType = (grant?: AccessGrant): string => {
  if (isRoleGrant(grant)) return 'Assumed Role';
  const managedBy = grant?.managed_by;
  if (!managedBy) return 'Policy';
  // toWordCase capitalises each word but leaves "AWS" as it is.
  return toWordCase(
    ['aws', 'customer'].includes(toLower(managedBy))
      ? `${managedBy} managed policy`
      : `${managedBy} policy`,
  );
};

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

/** The Mechanism cell's first line for a principal with several grants. */
export const getGrantsSummary = (grants: AccessGrant[] = []): string => {
  const roles = grants.filter(isRoleGrant).length;
  const policies = grants.length - roles;
  return [
    policies ? plural(policies, 'policy', 'policies') : '',
    roles ? `via ${plural(roles, 'role', 'roles')}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * The CRUD badges for the given counts, or "None". Each badge's tooltip carries
 * its count from `crud_level_counts_high`.
 */
export const renderHighPrivilegesCell = ({ value }: { value?: Dict<any> }) =>
  isEmpty(value) ? (
    <Center color="gray.400">None</Center>
  ) : (
    <Center>
      <HStack spacing={0.5}>
        {map(
          highPrivilegesConst,
          o =>
            (value?.[o] || value?.[o.toLowerCase()]) &&
            renderHighPrivilege(
              o,
              getHighPrivilegeColor(o.toLowerCase()),
              value?.[o] ?? value?.[o.toLowerCase()],
            ),
        )}
      </HStack>
    </Center>
  );

/**
 * A policy grant's badges. Its levels are exact but its counts are not (an
 * access line doesn't record which policy grants which permission), so the
 * badges carry no count.
 */
export const renderGrantLevels = (levels: string[] = []) => {
  const granted = levels.map(toLower);
  const shown = highPrivilegesConst.filter(o => granted.includes(toLower(o)));
  return isEmpty(shown) ? (
    <Center color="gray.400">None</Center>
  ) : (
    <Center>
      <HStack spacing={0.5}>
        {shown.map(o => (
          <React.Fragment key={o}>
            {renderHighPrivilege(o, getHighPrivilegeColor(o))}
          </React.Fragment>
        ))}
      </HStack>
    </Center>
  );
};
